import {
	forwardRef,
	PropsWithChildren,
	ReactElement,
	Ref,
	useContext,
	useMemo
} from "react";
import classNames from "classnames";
import { WeekViewContext } from "./WeekViewContext";
import styles from "./WeekViewCard.module.less";
import { Icon, IconValue } from "../Icon/Icon";
import { EMPTY_ARRAY, setTrackingId } from "client/Misc/utils";

export const WeekViewCardTitle = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div className={styles.title}>
			{ children }
		</div>
	);
};
WeekViewCardTitle.displayName = "WeekViewCardTitle";

export const WeekViewCardInfo = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div className={styles.info}>
			{ children }
		</div>
	);
};
WeekViewCardInfo.displayName = "WeekViewCardInfo";

const WeekViewCardRow = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div className={styles.row}>
			{ children }
		</div>
	);
};
WeekViewCardRow.displayName = "WeekViewCardRow";

const WeekViewCardRightAddition = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div className={styles.rightAddition}>
			{ children }
		</div>
	);
};
WeekViewCardRightAddition.displayName = "WeekViewCardRightAddition";

export type WeekViewCardStyle =
	| "default"
	| "success"
	| "warning"
	| "danger"
	| "secondary";

export type WeekViewCardProps = {
	style?: WeekViewCardStyle;
	title?: string;
	isSelected?: boolean;
	onClick?(): void;
	trackingId?: string;
} & ({
	isPlaceholder?: false | undefined;
	icons?: readonly IconValue[];
} | {
	isPlaceholder: true;
});

const WeekViewCardInternal = forwardRef<HTMLDivElement, PropsWithChildren<WeekViewCardProps>>(
	(
		props: PropsWithChildren<WeekViewCardProps>,
		ref: Ref<HTMLDivElement>
	): ReactElement => {
		const {
			style = "default",
			isPlaceholder = false,
			title,
			isSelected = false,
			onClick,
			children,
			trackingId
		} = props;

		const { mode } = useContext(WeekViewContext);

		const icons: readonly IconValue[] = props.isPlaceholder
			? EMPTY_ARRAY
			: props.icons ?? EMPTY_ARRAY;

		const transformedIcons = useMemo(
			(): readonly IconValue[] => {
				if (!isSelected) {
					return icons;
				}

				return icons.map(
					(x): IconValue => {
						if (typeof x === "string" || x.kind !== "font") {
							return x;
						}

						return {
							...x,
							color: undefined
						};
					}
				);
			},
			[icons, isSelected]
		);

		return (
			<div
				{...setTrackingId(trackingId)}
				className={classNames(
					styles.root,
					[cardStyleToClassNameMapping[style]],
					{
						[styles.root_selected]: isSelected,
						[styles.root_clickable]: Boolean(onClick),
						[styles.root_inHourMode]: mode === "hours",
						[styles.root_placeholder]: isPlaceholder
					}
				)}
				ref={ref}
				onClick={onClick}
				title={title}
			>
				<div className={styles.content}>
					{ children }
				</div>
				{transformedIcons.length > 0 && (
					<ul className={styles.icons}>
						{transformedIcons.map((icon, i) => (
							<li key={i}>
								<Icon
									key={i}
									value={icon}
								/>
							</li>
						))}
					</ul>
				)}
			</div>
		);
	}
);
WeekViewCardInternal.displayName = "WeekViewCard";

const cardStyleToClassNameMapping: { [k in WeekViewCardStyle]: string | undefined; } = {
	["default"]: undefined,
	["success"]: styles.root_success,
	["warning"]: styles.root_warning,
	["danger"]: styles.root_danger,
	["secondary"]: styles.root_secondary
};

type WeekViewWithSubComponents = typeof WeekViewCardInternal & {
	Title: typeof WeekViewCardTitle;
	Info: typeof WeekViewCardInfo;
	RightAddition: typeof WeekViewCardRightAddition;
	Row: typeof WeekViewCardRow;
};

export const WeekViewCard: WeekViewWithSubComponents = WeekViewCardInternal as unknown as WeekViewWithSubComponents;
WeekViewCard.Title = WeekViewCardTitle;
WeekViewCard.Info = WeekViewCardInfo;
WeekViewCard.RightAddition = WeekViewCardRightAddition;
WeekViewCard.Row = WeekViewCardRow;
